import type { I18nKey } from "./es";

// Available immediately with the other UI dictionaries.
export const en: Record<I18nKey, string> = {
  "credits.extraShort": "+{count} extra",
  "credits.title": "Additional AI credits",
  "credits.offer": "100 AI calls · €10",
  "credits.description": "One-time purchase, available to everyone. Credits never expire and are used after the calls included in your plan.",
  "credits.unit": "One credit extracts one callsheet. Failed attempts do not deduct credits; successfully extracting a document again uses another credit.",
  "credits.balance": "Extra credits available: {count}",
  "credits.balanceUnknown": "Credit balance has not been verified yet.",
  "credits.buy": "Buy 100 credits for €10",
  "credits.opening": "Opening payment…",
  "credits.purchaseError": "Could not open payment. Please try again.",
  "credits.confirmed": "Payment confirmed: 100 credits have been added.",
  "credits.pending": "Payment confirmation is pending. If you have already paid, you do not need to buy again.",
  "credits.checkPayment": "Check payment",
  "credits.refunded": "This payment has a refund. Your balance has been updated.",
  "projects.showTrips": "Show trips in {name}",
  "projects.moveTripHint": "Drag a trip onto another project or choose “Move to…”. The source is removed automatically if no trips, independent documents or expenses remain.",
  "projects.noTripsInFilter": "No trips match this filter.",
  "projects.dragTrip": "Drag trip dated {date}",
  "projects.moveTripLabel": "Move trip dated {date}",
  "projects.moveTripTo": "Move to…",
  "projects.moveTripDone": "Trip moved to “{name}”.",
  "projects.moveTripRemoved": "The source project was empty and has been removed.",
  "projects.moveTripRetained": "The source project still has independent documents or expenses and has been kept.",
  "projects.moveTripFailed": "The move could not be confirmed. Refresh the list before trying again.",
  "projects.moveTripUnavailable": "Moving trips requires a database update. The trip has not been moved.",
  "bulk.retryLimit": "You have reached the limit of 3 retries. Review and correct the document manually.",
  "bulk.retryBusy": "This document is already being extracted or both processing slots are busy. Wait for one extraction to finish before retrying.",
  "bulk.retryDocument": "Retry extraction",
  "bulk.retryDocumentConfirm": "Retry extraction of “{name}” using the uploaded file? A successful extraction will count towards your quota.",
  "bulk.queueStartFailed": "The files are saved, but the queue start could not be confirmed. Reopen this window to continue.",
  "bulk.backgroundProcessing": "You can close this window: uploaded documents continue processing in the background. Failed extractions are not retried automatically.",
  "bulk.waitForUpload": "Wait until uploading finishes and the batch start is confirmed.",
  "bulk.deleteDocument": "Delete",
  "bulk.deleteDocumentLabel": "Delete document {name}",
  "bulk.deleteDocumentConfirm": "Delete \"{name}\" and its original file? This cannot be undone.",
  "bulk.documentDeleted": "Document deleted.",
  "bulk.deleteDocumentError": "The document could not be deleted. It remains available so you can try again.",
  "plans.checking": "Checking subscription…",
  "plans.unavailable": "Your subscription could not be checked. Try again; you do not need to buy another plan.",
  "plans.statusUnavailable": "Subscription unverified",
  "ui.csvRows": "CSV: include column headers and at least one row.",
  "ui.batchMax": "Maximum {count} documents per batch",
  "ui.uploaded": "Uploaded {count} documents",
  "ui.uploadedProcess": "Uploaded {count} documents. Select \"Process now\" to start.",
  "ui.uploadFailedCount": "{count} documents failed to upload",
  "ui.cancelledCount": "Processing cancelled ({count} jobs)",
  "ui.startingCount": "Starting extraction of {count} document(s)…",
  "ui.processedTrips": "Processed trips ({count})",
  "ui.projectCreatedNamed": "Project \"{name}\" created",
  "ui.createdViaTrip": "Created from a trip",
  "ui.calendarImport": "Imported from Google Calendar",
  "ui.rowDate": "Row {row}: invalid date",
  "ui.rowProject": "Row {row}: project name is empty",
  "ui.rowAddress": "Row {row}: origin/destination are empty (and no base address is set)",
  "ui.rowKm": "Row {row}: invalid kilometres",
  "ui.rowStops": "Row {row}: too many stops",
  "ui.partialDelete": "Deleted {ok}/{total}. {failed} failed (some associated data could not be deleted).",
  "ui.totalsUnavailable": "Project totals could not be loaded. Contact support.",
  "ui.projectExistsNamed": "Project \"{name}\" already exists",
  "ui.reportLimit": "You have reached your plan’s report limit.",
  "ui.invalidTrip": "Invalid trip data. {issues}",
  "ui.profileSaving": "Saving…",
  "ui.profileSaved": "Profile saved",
  "ui.profileFailed": "Could not save your profile.",
  "ui.emptyFile": "{name}: the file is empty.",
  "ui.largeFile": "{name}: exceeds the 50 MB file limit.",
  "ui.unclosedQuotes": "A cell has an unclosed quotation mark. Check the text before saving.",
  "ui.duplicateColumns": "Some columns have the same name. Rename them before importing.",
  "ui.damagedOffice": "The Office document is damaged.",
  "ui.complexOffice": "This Office document is too complex. Split it into several files.",
  "ui.expandedOffice": "The Office document contains too much decompressed data. Split it into several files.",
  "ui.spreadsheetType": "{name}: select CSV, TSV or Excel (.xlsx, .xls).",
  "ui.invalidWorkbook": "{name}: not a valid Excel workbook.",
  "ui.noSheets": "{name}: no visible sheets contain data.",
  "ui.invalidLogin": "Incorrect email or password.",
  "ui.unexpected": "An error occurred. Please try again.",
  "ui.googleConfiguration": "Could not connect to Google. Try again or contact support.",
  "ui.security": "Security",
  "ui.passwordHint": "Set or update your password to sign in with email.",
  "ui.changePassword": "Change password",
  "ui.geminiDefault": "Gemini AI (default)",
  "ui.model": "OpenRouter model",
  "ui.selectModel": "Select a model",
  "ui.loadingModels": "Loading models…",
  "ui.visionModels": "These models support image and PDF recognition.",
  "ui.lastExtraction": "Latest AI extraction",
  "ui.checkingProvider": "Checking the provider used…",
  "ui.provider": "Provider:",
  "ui.modelLabel": "Model:",
  "ui.actualProvider": "Underlying provider:",
  "ui.date": "Date:",
  "ui.noExtraction": "No extraction with provider details has been recorded yet.",
  "ui.disconnect": "Disconnect",
  "ui.changelog": "Change log",
  "ui.noUpdates": "No update channel has been configured yet.",
  "ui.noChangelog": "The change log is not yet available in the app.",
  "ui.noSupport": "The support channel has not been configured yet.",
  "ui.uploadCallsheet": "Upload callsheet",
  "ui.deleteCallsheet": "Delete this callsheet? Its associated data will also be deleted.",
  "ui.alreadyProcessing": "This document is already being processed",
  "ui.reprocess": "This document has already been processed. Process it again? This will use another extraction from your quota.",
  "ui.quotaPreserved": "No quota remains. The document and previous results have been kept.",
  "ui.quotaUnavailable": "Your quota could not be checked. The document has been kept; try again later.",
  "ui.processingCancelled": "Processing cancelled; document kept",
  "ui.noPendingDocuments": "There are no documents waiting to be processed",
  "ui.reviewProcessed": "Review the automatically extracted trips and save them together",
  "ui.processAI": "Process with AI",
  "ui.producerExample": "e.g. Film Production XY",
  "ui.loading": "Loading…",
  "ui.templateExample": "e.g. Visit to client X",
  "ui.routeRequired": "The route needs a starting point and destination",
  "ui.templateFailed": "Could not save the template",
  "ui.dateRequired": "Select a date",
  "ui.fillRoute": "Enter the starting point and destination",
  "ui.commands": "Commands",
  "ui.commandHint": "Search and run commands",
  "ui.noRecentTrips": "No recent trips.",
  "ui.offline": "You are offline. Saved data may be available, but changes cannot sync until you reconnect.",
  "ui.captchaFailed": "Security verification could not be completed. Check your connection.",
  "ui.retry": "Try again",
  "ui.emissionsLoading": "Loading emissions data…",
  "ui.maxStops": "Maximum of 8 intermediate stops",
  "ui.templateRouteRequired": "The template needs a starting point and destination",
  "ui.deleteStop": "Remove stop",
  "ui.projectDeleteFailed": "Could not delete the project.",
  "ui.projectExists": "A project with this name already exists",
  "ui.projectUpdated": "Project updated",
  "ui.projectCreated": "Project created",
  "ui.tripDeleteFailed": "Could not delete the trip.",
  "ui.tripUpdateFailed": "Could not update the trip. The previous version has been kept.",
  "ui.distance": "Distance:",
  "ui.eventTitleRequired": "Enter a title for the event",
  "ui.eventDates": "Check the start and end dates and times",
  "ui.eventEnd": "The end time must be after the start time",
  "ui.eventCreated": "Event created",
  "ui.enterEmail": "Enter your email",
  "ui.recoveryEmailHint": "Enter your email above so we can send you a recovery link.",
  "ui.emailSent": "Email sent",
  "ui.checkInbox": "Check your inbox to reset your password.",
  "ui.sendFailed": "Could not send",
  "ui.googleFailed": "Could not sign in with Google",
  "ui.passwordUpdated": "Password updated",
  "ui.newPasswordLogin": "You can now sign in with your new password.",
  "ui.authError": "Authentication error",
  "ui.noGoogleResponse": "No response received from Google",
  "ui.retryLogin": "Try again from the sign-in page.",
  "ui.loginFailed": "Sign-in could not be completed",
  "ui.checkGoogleSetup": "Check the Google configuration in Supabase.",
  "ui.invalidSession": "Invalid session",
  "ui.sessionFailed": "The session could not be restored after sign-in.",
  "ui.verifyingAuth": "Verifying authentication…",
  "ui.invalidLink": "Invalid link",
  "ui.newRecoveryEmail": "Request a new email to reset your password.",
  "ui.backLogin": "Back to sign in",
  "ui.backLogin2": "Back to sign in",
  "ui.secureReset": "Secure password reset",
  "ui.confirmReset": "Confirm that you want to continue to create a new Fahrtenbuch Pro password.",
  "ui.anotherLink": "Request another link",
  "ui.continueSecure": "Continue securely",
  "ui.verificationSlow": "Verification is taking too long. Check your connection and try again.",
  "ui.expiredLink": "This link has expired or has already been used. Request a new recovery email.",
  "ui.verifyLinkFailed": "The link could not be verified. Request a new recovery email.",
  "ui.supabaseMissing": "Supabase is not configured",
  "ui.supabaseEnvMissing": "Supabase environment variables are missing.",
  "ui.sessionMissing": "Session not found",
  "ui.openRecoveryEmail": "Open the recovery link from your email.",
  "ui.passwordMismatch": "The passwords do not match",
  "ui.passwordShort": "Password is too short",
  "ui.passwordMin": "Use at least 8 characters.",
  "ui.passwordEmailLogin": "You can now sign in with your email and password.",
  "ui.updateFailed": "Could not update",
  "ui.checkCurrentPassword": "Check your current password and try again.",
  "ui.unexpectedError": "Unexpected error",
  "ui.resetPassword": "Reset password",
  "ui.recoverySessionHint": "Open the recovery link we emailed you. If you already opened it but have no session here, try again.",
  "ui.verificationNeeded": "Verification required",
  "ui.startSecureChange": "Start the change in Settings or use the secure link sent to your email.",
  "ui.backDashboard": "Back to dashboard",
  "ui.secureLinkSent": "Secure link sent",
  "ui.openSecureEmail": "Open the email to securely set a password.",
  "ui.linkSendFailed": "Could not send the link",
  "ui.retryLater": "Wait a few minutes and try again.",
  "ui.setPassword": "Set password",
  "ui.externalAccount": "Your account uses an external provider. To protect it, we will send a one-time link to your verified email.",
  "ui.sendSecureLink": "Send secure link",
  "ui.newPassword": "New password",
  "ui.choosePassword": "Choose a new password for your account.",
  "ui.currentPassword": "Current password",
  "ui.password": "Password",
  "ui.confirmPassword": "Confirm password",
  "ui.save": "Save",
  "ui.errorTitle": "Something went wrong",
  "ui.errorBody": "The app encountered an unexpected error. Reload or try again.",
  "ui.reload": "Reload",
  "ui.errorSupport": "If the problem persists, check your connection or contact support.",
  "ui.reloadConfirm": "This tab will reload. Unsaved changes will be lost. Continue?",
  "ui.callsheetUploadFailed": "Could not upload the callsheet",
  "ui.home": "Home",

  "modal.close": "Close",
  "modal.addDocuments": "Add documents",
  "modal.addExpense": "Add expense",
  "modal.details": "Trip details",
  "modal.noTrips": "This project has no trips yet.",
  "modal.options": "Trip options",
  "modal.uploadHelp": "Formats and limits",
  "modal.select": "Select",
  "modal.process": "Process",
  "modal.review": "Review",
  "modal.files": "Files",
  "modal.text": "Paste text",
  "modal.vehicle": "Vehicle",
  "modal.costs": "Rates and costs",
  "modal.security": "Security",
  "modal.integrations": "Integrations",
  "modal.summary": "Overview",
  "modal.documents": "Documents",
  "modal.calculate": "Calculate distance",
  "modal.uploadLimit": "Up to {count} files · 50 MB per file",
  "modal.batchReview": "Open a document to check its details and route.",
  "callsheetReview.deletePartial": "Deleted {deleted} items. {failed} could not be deleted and remain selected for retry.",
  "tripDetail.editInvalid": "Check the date, complete every address, and enter valid distances and amounts.",
  "tripDetail.editInvalidDocument": "Check the date and keep at least one valid location. Complete or remove empty locations. Distance, passengers and expenses are optional; any values entered must be valid.",
  "callsheetReview.title": "Review",
  "callsheetReview.description": "Extracted data awaiting confirmation.",
  "callsheetReview.detectedDate": "Detected date",
  "callsheetReview.incompleteDate": "Date needs completion or confirmation",
  "callsheetReview.checkData": "Review extracted data",
  "callsheetReview.details": "View details",
  "callsheetReview.open": "View document",
  "callsheetReview.edit": "Complete trip",
  "callsheetReview.loadError": "Documents awaiting review could not be loaded. Please try again.",
  "callsheetReview.retry": "Retry",

  "updates.available": "New version available",
  "updates.description": "Update once you have saved your work. Other tabs will stay open.",
  "updates.action": "Update",
  "updates.closeEditor": "Save or close the open form before updating.",
  "updates.confirm": "This tab will reload. Unsaved changes will be lost. Continue?",
  "updates.failed": "The update could not be activated. Your tab is still open; try again.",
  "expenseScan.deleteConfirm": "This receipt will be permanently deleted",
  "expenseScan.deleteRecoveryError": "The deletion could not be completed and the receipt link could not be restored. Deletion has not been confirmed. Reload the trip or project and contact support if the receipt is still missing.",
  "uploads.invalidNameTitle": "Invalid file name",
  "uploads.invalidNameBody": "“{name}” contains folder separators or control characters. Select a file with a valid name.",
  "uploads.fileNameHint": "Keep your original file names, including #, accents and spaces.",
  "uploads.ownershipError": "We could not verify that this file belongs to your account. Check its name and upload it again while signed in. If it fails again, contact support.",
  "uploads.callsheetFormats": "PDF, JPG/JPEG, PNG, WebP, HEIC/HEIF, Excel (XLSX/XLS), CSV, TSV, TXT and Word (DOCX).",
  "uploads.callsheetLimits": "Up to {count} files per batch and 50 MB per file. Your AI provider may apply page, content or time limits.",
  "bulk.manualReview": "Review and edit rows before saving. Missing distances are saved as 0; you can correct them in Trips.",
  "bulk.removeFile": "Remove file",
  "bulk.removeRow": "Remove row",
  "bulk.rows": "rows",
  "bulk.manualDuplicates": "Skipped {count} duplicate or already saved trips.",
  "bulk.manualDropHint": "Drop CSV or Excel here, or click to select. Up to 50 MB per file.",
  "bulk.saveManual": "Save reviewed trips",
  "bulk.reviewPastedCsv": "Review pasted CSV",
  "bulk.aiPasteLabel": "Email or message text",
  "bulk.aiPasteHint": "Paste the content to extract here…",
  "bulk.aiAddText": "Add text to the list",
  "bulk.aiTextQuotaHint": "Each text added counts as one document. Quota is used when processing with AI.",
  "uploads.callsheetConversion": "For emails and messages, paste the text in the AI tab. No email file download needed.",
  "bulk.csvExcelHint": "CSV, TSV and Excel (.xlsx, .xls). Visible worksheets are read; review before saving. No AI usage.",
  "expenseScan.fileHint": "Images up to 10 MB, one per selection. You can add multiple receipts to the same expense. This scanner does not accept PDFs.",
  "nav.dashboard": "Dashboard",
  "nav.trips": "Trips",
  "nav.projects": "Projects",
  "nav.reports": "Reports",
  "nav.calendar": "Calendar",
  "nav.advanced": "Advanced",
  "nav.settings": "Settings",
  "nav.menu": "Menu",
  "nav.expand": "Expand",
  "nav.collapse": "Collapse",

  "nav.logout": "Logout",
  "dashboard.welcomeBack": "Welcome back, {name}",
  "dashboard.welcomeBackPrefix": "Hello,",
  "dashboard.greetingMorning": "Good morning,",
  "dashboard.greetingAfternoon": "Good afternoon,",
  "dashboard.greetingEvening": "Good evening,",
  "dashboard.subtitle": "Here’s your mileage overview for this month",
  "dashboard.totalDistance": "Total distance",
  "dashboard.activeProjects": "Active projects",
  "dashboard.co2Emissions": "CO₂ emissions",
  "dashboard.thisMonthVsPrevious": "this month vs previous",
  "dashboard.vsLastMonth": "vs last month",
  "dashboard.viewTrips": "View trips",
  "dashboard.viewProjects": "View projects",
  "dashboard.viewCo2": "View CO₂",
  "dashboard.thisMonth": "This month",
  "dashboard.lastMonth": "Last month",
  "dashboard.trend": "Trend",
  "dashboard.totalKm": "Total km",
  "dashboard.status": "Status",
  "dashboard.equivalentTrees": "Equivalent trees",
  "dashboard.equivalentTreesTooltip": "Illustrative equivalent: about 21 kg of CO₂ per mature tree over one year. Varies by species and conditions; this is not an actual carbon offset.",
  "dashboard.excellent": "Excellent",
  "dashboard.good": "Good",
  "dashboard.improvable": "Needs improvement",
  "dashboard.warnings": "Warnings",
  "dashboard.noWarnings": "No warnings",
  "tripWarning.invalidDate": "Invalid date",
  "tripWarning.noDate": "No date",
  "tripWarning.incompleteRoute": "Incomplete route",
  "tripWarning.missingOriginDestination": "Missing origin/destination",
  "tripWarning.zeroDistance": "Distance 0 km",
  "tripWarning.noDistance": "No distance",
  "tripWarning.improbableDistance": "Improbable distance",
  "tripWarning.noProject": "No project",
  "tripWarning.assignProject": "Assign a project to group and report",
  "tripWarning.duplicateTrip": "Duplicate trip",
  "tripWarning.sameDateAndRoute": "Same date and route",
  "trips.title": "Trips",
  "trips.subtitle": "Manage and track all your business trips",
  "trips.delete": "Delete",
  "trips.bulkUpload": "Bulk upload",
  "trips.addTrip": "Add trip",
  "trips.project": "Project",
  "trips.allProjects": "All projects",
  "trips.all": "All",
  "trips.year": "Year",
  "trips.date": "Date",
  "trips.route": "Route",
  "trips.co2": "CO₂",
  "trips.passengers": "Passengers",
  "trips.reimbursement": "Reimbursement",
  "trips.expenses": "Expenses",
  "trips.receipts": "Receipts",
  "trips.totalCost": "Total cost",
  "trips.distance": "Distance",
  "trips.viewMap": "View map",
  "trips.addToCalendar": "Add to calendar",
  "trips.edit": "Edit",
  "trips.showing": "Showing {count} trips",
  "trips.of": "of",
  "trips.loadMore": "Load more",
  "trips.total": "Total: {km} km",
  "trips.sortByDate": "Sort by date ({order})",
  "trips.sortAsc": "ascending",
  "trips.sortDesc": "descending",
  "trips.toastAddedToCalendarTitle": "Added to calendar",
  "trips.toastAddedToCalendarBody": "Trip to {destination} on {date} added to calendar.",
  "trips.toastTripsDeletedTitle": "Trips deleted",
  "trips.toastTripsDeletedBody": "{count} trip(s) deleted.",
  "trips.toastTripCreatedTitle": "Trip saved",
  "trips.toastTripCreatedBody": "The trip was saved successfully.",
  "trips.continuation": "Continuation",
  "trips.returnTrip": "Return trip",
  "trips.toastTripUpdatedTitle": "Trip updated",
  "trips.toastTripUpdatedBody": "Changes were saved successfully.",
  "trips.calendarNotConnected": "Google Calendar isn't connected. Connect it in Settings > APIs.",
  "trips.selectTrip": "Select trip {id}",
  "projects.title": "Projects",
  "projects.subtitle": "Organize trips by project for easy reporting",
  "projects.delete": "Delete",
  "projects.newProject": "New project",
  "projects.createNewProject": "Create new project",
  "projects.projectName": "Project name",
  "projects.company": "Company / Client",
  "projects.companyPlaceholder": "Company name",
  "projects.description": "Description",
  "projects.descriptionPlaceholder": "Brief project description",
  "projects.ratePerKm": "Rate per km (€)",
  "projects.ratePerPassenger": "Rate per passenger (€)",
  "projects.createProject": "Create project",
  "projects.filterProducer": "Producer",
  "projects.searchPlaceholder": "Search projects...",
  "projects.year": "Year",
  "projects.allTime": "All time",
  "projects.selectAll": "Select all",
  "projects.tableDate": "Date",
  "projects.tableProject": "Project",
  "projects.tableCompany": "Company / Producer / Client",
  "projects.tableTrips": "Trips",
  "projects.tableTotalKm": "Total distance",
  "projects.tableCo2": "CO₂ (kg)",
  "projects.tableDocuments": "Documents / Call Sheets",
  "projects.tableInvoices": "Invoice",
  "projects.tableCost": "Reimbursement",
  "projects.viewDetails": "View details",
  "projects.edit": "Edit",
  "projects.emptyTitle": "No projects found",
  "projects.emptyTryAdjust": "Try adjusting your search query",
  "projects.emptyCreateFirst": "Create your first project to get started",
  "projects.summaryCount": "{count} projects",
  "projects.summaryTotal": "Total: {km} km",
  "projects.toastDeletedTitle": "Projects deleted",
  "projects.toastDeletedBody": "{count} project(s) deleted.",
  "projects.selectProject": "Select project {name}",
  "tripModal.addTitle": "Add trip",
  "tripModal.editTitle": "Edit trip",
  "tripModal.date": "Date",
  "tripModal.project": "Project",
  "tripModal.selectProject": "Select project",
  "tripModal.route": "Route",
  "tripModal.addStop": "Add stop",
  "tripModal.specialOrigin": "Special origin",
  "tripModal.specialOriginBase": "Start from my base address",
  "tripModal.specialOriginContinue": "Continue from last destination",
  "tripModal.specialOriginReturn": "Return to my base address (from last destination)",
  "tripModal.passengers": "Passengers",
  "tripModal.fuelLiters": "Fuel (L)",
  "tripModal.evKwhUsed": "Electric consumption (kWh)",
  "tripModal.distance": "Distance (km)",
  "tripModal.rate": "Trip rate (€/km)",
  "tripModal.purpose": "Purpose",
  "tripModal.purposePlaceholder": "Trip purpose",
  "tripModal.toll": "Toll (€)",
  "tripModal.parking": "Parking (€)",
  "tripModal.otherExpenses": "Other (€)",
  "tripModal.fuel": "Fuel (€)",
  "tripModal.save": "Save trip",
  "tripModal.update": "Update trip",
  "tripModal.origin": "Origin",
  "tripModal.destination": "Destination",
  "tripModal.stop": "Stop",
  "tripModal.originPlaceholder": "Origin location",
  "tripModal.destinationPlaceholder": "Destination location",
  "tripModal.stopPlaceholder": "Intermediate stop",
  "tripModal.projectSearchPlaceholder": "Search or create project...",
  "tripModal.projectNotFound": "Not found.",
  "tripModal.createProjectNamed": "Create \"{name}\"",
  "tripModal.useAsClient": "Use \"{name}\" as client",
  "calendar.title": "Calendar",
  "calendar.subtitle": "Sync events from Google Calendar",
  "calendar.refresh": "Refresh",
  "calendar.connectGoogle": "Connect Google",
  "calendar.calendars": "Calendars",
  "calendar.notConnected": "Not connected",
  "calendar.notConnectedBody": "Connect your Google Calendar to sync events and create trips from calendar entries.",
  "calendar.connect": "Connect",
  "calendar.disconnect": "Disconnect",
  "calendar.insufficientPermissionsTitle": "Insufficient permissions",
  "calendar.insufficientPermissionsBody": "Google Calendar permissions need to be updated to list your calendars. Disconnect and reconnect.",
  "calendar.reconnect": "Reconnect",
  "calendar.createEventTitle": "Create event",
  "calendar.eventTitleLabel": "Title",
  "calendar.eventTitlePlaceholder": "Meeting / trip / reminder",
  "calendar.calendarLabel": "Calendar",
  "calendar.selectCalendarPlaceholder": "Select a calendar",
  "calendar.startLabel": "Start",
  "calendar.endLabel": "End",
  "calendar.cancel": "Cancel",
  "calendar.creating": "Creating...",
  "calendar.create": "Create",
  "calendar.loading": "Loading...",
  "calendar.more": "+{count} more",
  "calendar.weekdays": "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
  "calendar.importEventTitle": "Import event as trip",
  "calendar.importEventDescription": "Create a trip based on this calendar event",
  "calendar.eventLocation": "Location",
  "calendar.eventDescription": "Description",
  "calendar.importWillCreate": "Will create a trip with:",
  "calendar.importClientFromTitle": "Client/Company: event title",
  "calendar.importRouteFromBase": "Route: from base address (round trip)",
  "calendar.importProjectManual": "Project: set to 'Unknown' (editable later)",
  "calendar.importDistanceCalculated": "Distance: calculated automatically",
  "calendar.viewInGoogle": "View in Google",
  "calendar.importAsTrip": "Import as trip",
  "calendar.importing": "Importing...",
  "calendar.importSuccess": "Trip imported successfully",
  "calendar.importError": "Could not import event",
  "calendar.importNoLocations": "No locations found in event. Add a location or description with origin and destination.",
  "calendar.importNoDistance": "Could not calculate distance. Check locations.",
  "calendar.importNeedBaseAddress": "Complete your base address, city and country in Settings before importing the event.",

  // Advanced
  "advanced.title": "Advanced",
  "advanced.subtitle": "Advanced tools and analytics",
  "advanced.routesTitle": "Route templates",
  "advanced.routesBody": "Manage your frequent routes and predefined templates",
  "advanced.costsTitle": "Cost analysis",
  "advanced.costsBody": "Analyze expenses, fuel and maintenance",
  "advanced.emissionsTitle": "CO₂ emissions",
  "advanced.emissionsBody": "Track carbon footprint and environmental efficiency",

  "auth.tagline": "Professional mileage tracking",
  "auth.featureTrips": "🚗 Track every trip",
  "auth.featureTripsBody": "Automatically log distances with GPS or AI-powered extraction from callsheets.",
  "auth.featureReports": "📊 Generate reports",
  "auth.featureReportsBody": "Create reports ready for download and print.",
  "auth.featureImport": "🤖 AI-powered import",
  "auth.featureImportBody": "Extract trip data from PDFs, images, and callsheets automatically.",
  "auth.login": "Login",
  "auth.signUp": "Sign up",
  "auth.pleaseSignIn": "Please sign in",
  "auth.fullName": "Full name",
  "auth.password": "Password",
  "auth.forgotPassword": "Forgot password?",
  "auth.createAccount": "Create account",
  "auth.verifyEmailTitle": "Verify your email",
  "auth.verifyEmailBody": "We sent you an email to verify your account. Check your inbox (and spam).",
  "auth.orContinueWith": "Or continue with",
  "auth.continueWithGoogle": "Continue with Google",
  "auth.byContinuing": "By continuing, you agree to our",
  "auth.and": "and",
  "auth.terms": "Terms of Service",
  "auth.privacy": "Privacy Policy",
  "auth.heroTitle": "Your Kilometergeld, on autopilot.",
  "auth.heroBullet1": "Upload the shoot day's call sheet",
  "auth.heroBullet2": "The AI creates the trips with route and km",
  "auth.heroBullet3": "Monthly PDF report for production",
  "auth.heroFooter": "Film & TV crew in Austria · shoots in DE, CZ and HU",
  "auth.heroCardTitle": "Kilometergeld-Abrechnung",
  "auth.heroCardMeta": "June 2026 · 26 trips · 3 receipts",
  "auth.heroCardTotalLabel": "TOTAL JUNE",
  "auth.welcomeBackTitle": "Sign in",
  "auth.welcomeBackSubtitle": "Access your existing account.",
  "auth.createAccountTitle": "Create your account",
  "auth.createAccountSubtitle": "Start free. No card required.",
  "auth.googleAccountNotFoundTitle": "This account does not exist yet",
  "auth.googleAccountNotFoundBody": "To register, select \"Start free\" and continue with Google.",
  "auth.googleAccountCheckFailed": "We could not verify the Google account. Please try again.",
  "auth.orWithEmail": "or with your email",
  "auth.noAccount": "No account yet?",
  "auth.startFree": "Start free",
  "auth.haveAccount": "Already have an account?",
  "auth.email": "Email",
  "auth.emailPlaceholder": "you@email.com",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "common.allCompanies": "All Companies",
  "common.comingSoon": "Coming soon",
  "notFound.message": "Oops! Page not found",
  "notFound.returnHome": "Return to home",
  "chart.kmByProject": "Kilometers by project",
  "chart.kmByWeek": "Kilometers by week",
  "chart.kmByMonth": "Kilometers by month",
  "chart.kmByYear": "Kilometers by year",
  "chart.distance": "Distance",
  "chart.filterProject": "Project",
  "chart.filterAll": "All",
  "chart.filterWeek": "Week",
  "chart.filterMonth": "Month",
  "chart.filterYear": "Year",
  "chart.filterAllProjects": "All",
  "chart.noData": "No data to display",
  "dashboard.recentTrips": "Recent trips",
  "dashboard.viewAll": "View all",
  "bulk.aiExtraction": "AI extraction",
  "bulk.title": "Bulk upload assistant",
  "bulk.subtitle": "Import trips from CSV or Excel, or extract documents and messages with AI.",
  "bulk.tabCsv": "CSV / Excel · Manual",
  "bulk.tabAi": "AI extraction",
  "bulk.csvInstructionsTitle": "CSV import instructions",
  "bulk.csvInstructionsRequired": "Required headers: 'date', 'projectName', 'origin', 'destination'. Optional: 'reason', 'distance', and 'client'/'company'/'producer'.",
  "bulk.csvInstructionsStops": "Columns between 'origin' and 'destination' (in CSV order) are treated as intermediate stops. Columns 'client'/'company'/'producer' are treated as project metadata. If 'origin' or 'destination' are empty, your base address will be used by default.",
  "bulk.csvInstructionsSeparator": "The file may be comma (,) or semicolon (;) separated. The importer will detect it automatically.",
  "bulk.selectCsvFile": "Select CSV or Excel",
  "bulk.importFromDrive": "Import from Google Drive",
  "bulk.drivePickerTitle": "Select a CSV in Google Drive",
  "bulk.drivePickerTitlePdf": "Select PDFs from Google Drive",
  "bulk.drivePickerTitleCallsheets": "Select callsheets from Google Drive (PDF or image)",
  "bulk.downloadingFromDrive": "Downloading from Drive...",
  "bulk.importPdfFromDrive": "Import PDFs from Google Drive",
  "bulk.errorDrivePickerNotConfigured": "Missing VITE_GOOGLE_PICKER_API_KEY to open the Google Drive picker.",
  "bulk.or": "or",
  "bulk.pasteCsv": "Paste CSV text",
  "bulk.processPasted": "Process pasted text",
  "bulk.cancel": "Cancel",
  "bulk.toastProcessingCancelled": "Extraction cancelled when closing the modal.",
  "bulk.aiCancelExtraction": "Cancel extraction",
  "bulk.toastBackgroundProcessing": "Extraction continues in the background. Reopen the assistant to review it.",
  "bulk.aiDropTitle": "Drop documents here",
  "bulk.aiDropSubtitle": "Call sheets as PDF or images (JPG/JPEG, PNG, WebP, HEIC and HEIF)",
  "bulk.aiChangeFilesHint": "Click or drop more files to add them.",
  "bulk.aiFilesSelected": "{count} files selected",
  "bulk.aiDescription": "Upload your call sheets as PDF or photo. AI will automatically extract dates, locations, project and distances.",
  "bulk.aiDisclaimer": "AI can make mistakes. It is the user's responsibility to verify that the extracted information is correct before saving.",
  "bulk.aiProcessingTitle": "Analyzing documents...",
  "bulk.aiProcessingHint": "This can take a few seconds.",
  "bulk.aiProcessingProgress": "Completed {done}/{total} | Pending {pending} | Failed {failed}",
  "bulk.aiParallelReviewTitle": "Review documents",
  "bulk.aiParallelReviewStats": "Ready {ready} · To review {review} · Saved {saved} · Pending {pending} · Failed {failed} · Cancelled {cancelled}",
  "bulk.back": "Back",
  "bulk.saveAll": "Save all",
  "bulk.statusSaved": "Saved",
  "bulk.statusReady": "Ready",
  "bulk.statusProcessing": "Processing",
  "bulk.statusQueued": "Queued",
  "bulk.statusFailed": "Failed",
  "bulk.statusNeedsReview": "Review",
  "bulk.statusOutOfQuota": "Limit reached",
  "bulk.statusProcessingEllipsis": "Processing...",
  "bulk.statusQueuedEllipsis": "Queued...",
  "bulk.loadingResults": "Loading results...",
  "bulk.outOfQuotaTitle": "Extraction quota reached",
  "bulk.outOfQuotaMessage": "No calls remain in your plan or extra balance. Buy 100 credits for €10 or check your plan renewal.",
  "bulk.outOfQuotaButton": "Buy credits or view plans",
  "bulk.batchLimitTitle": "Only 1 document at a time",
  "bulk.batchLimitMessage": "Up to {count} documents per upload. Split your files into several batches.",
  "bulk.docProcessFailed": "Couldn't process this document.",
  "bulk.optimizingHint": "Optimizing route and addresses...",
  "bulk.locationsRouteLabel": "Locations / Route ({count})",
  "bulk.originLabel": "Origin",
  "bulk.destinationLabel": "Destination",
  "bulk.notSet": "Not set",
  "bulk.producerLabel": "Production company",
  "bulk.errorProcessOneDoc": "Couldn't process a document",
  "bulk.errorSaveTrip": "Couldn't save the trip",
  "bulk.toastReusedDocs": "Reused {count} already uploaded document(s)",
  "bulk.toastAlreadySavedDocs": "{count} document(s) are already saved. They won't be duplicated.",
  "bulk.toastSavedTrips": "Saved {count} trips",
  "bulk.purposeDefault": "Shoot",
  "bulk.purposeWithProducer": "Shoot: {producer}",
  "bulk.originalDocumentName": "Original document",
  "bulk.aiProcess": "Process with AI",
  "bulk.examplePlaceholder": "Example:\ndate,projectName,reason,origin,stop1,destination,km\n20-09-2025,My Project,Shooting,\"Plaza Mayor 1, 28012 Madrid\",\"Puerta del Sol 1, 28013 Madrid\",\"Plaza de Cibeles 1, 28014 Madrid\",2.5",
  "bulk.errorNoValidTrips": "No valid trips found to import",
  "bulk.toastImportedTrips": "Imported {count} trips",
  "bulk.toastFailedTrips": "Failed {count} trips",
  "bulk.toastCsvLoaded": "CSV loaded",
  "bulk.errorCsvRead": "Couldn't read the CSV",
  "bulk.errorLoginDrive": "Sign in to import from Drive",
  "bulk.promptDriveLink": "Paste the Google Drive link or the CSV fileId",
  "bulk.errorDetectFileId": "Couldn't detect the fileId",
  "bulk.errorDriveDownload": "Drive download failed",
  "bulk.toastCsvImportedDrive": "CSV imported from Drive",
  "bulk.errorMaxDocuments": "Maximum 20 documents at a time",
  "bulk.errorOnlyPdf": "Select PDF, images, Excel, CSV, TSV, TXT or DOCX.",
  "bulk.errorNotAuthenticated": "You're not authenticated",
  "bulk.errorUploadNone": "Couldn't upload any document",
  "bulk.toastUploadedDocs": "Uploaded {count} documents",
  "bulk.toastFailedDocs": "Failed {count} documents",
  "bulk.errorUploadDoc": "Error uploading document",
  "bulk.noLocationsFound": "No locations found.",
  "bulk.saveTrip": "Save trip",

  // Plan limits
  "limits.maxTripsReached": "You've reached the active trips limit. Delete some trips or upgrade your plan.",
  "limits.maxAITripsReached": "You've reached the AI-created trips limit. Delete some or upgrade your plan.",
  "limits.maxNonAITripsReached": "You've reached the manual trips limit. Delete some or upgrade your plan.",
  "limits.maxProjectsReached": "You've reached the active projects limit. Archive some or upgrade your plan.",
  "limits.maxStopsReached": "You've reached the stops per trip limit ({limit} maximum).",
  "limits.csvImportExceedsLimit": "The CSV contains more trips than you can import. Maximum: {limit}",
  "limits.calendarImportExceedsLimit": "The calendar contains more events than you can import. Maximum: {limit}",
  "limits.driveImportExceedsLimit": "Drive contains more files than you can import. Maximum: {limit}",
  "limits.tripLimitWarning": "Trips: {current}/{limit}",
  "limits.projectLimitWarning": "Projects: {current}/{limit}",
  "limits.aiLimitWarning": "AI extractions: {current}/{limit}",
  "limits.upgradePlan": "Upgrade plan",
  "limits.planBasic": "Basic Plan",
  "limits.planPro": "Pro Plan",
  "limits.maxTemplatesReached": "You've reached the route templates limit. Delete some or upgrade your plan.",
  "limits.maxReportsPerMonthReached": "You've reached the 1 report/month limit on the Basic plan. Upgrade to Pro for unlimited reports.",
  "plans.features.trips200": "200 active trips",

  // Plans page
  "nav.plans": "Plans",
  "plans.title": "Choose your plan",
  "plans.subtitle": "Your trips organized by project and the mileage report ready for production and the tax office.",
  "plans.billingMonthly": "Monthly",
  "plans.billingAnnual": "Annual",
  "plans.annualBadge": "-54%",
  "plans.perYear": "per year",
  "plans.annualEquiv": "That's €4.17/month — save 54%",
  "plans.cancelAnytime": "No commitment: cancel anytime.",
  "plans.basic.name": "Free",
  "plans.basic.description": "Unlimited trips and reports, free forever.",
  "plans.pro.name": "Pro",
  "plans.pro.description": "Dump your callsheets and bill without typing a thing.",
  "plans.forever": "forever",
  "plans.perMonth": "per month",
  "plans.currentPlan": "Current plan",
  "plans.upgradeToPro": "Upgrade to Pro",
  "plans.mostPopular": "Most popular",
  "plans.sidebar.free": "Free Plan",
  "plans.sidebar.upgrade": "Upgrade to Pro",
  "plans.sidebar.manage": "Manage plan",
  "plans.features.trips20": "20 active trips",
  "plans.features.trips2000": "2000 active trips",
  "plans.features.unlimitedTrips": "Unlimited trips",
  "plans.features.projects3": "3 projects",
  "plans.features.projects30": "30 projects",
  "plans.features.unlimitedProjects": "Unlimited projects",
  "plans.features.ai5": "5 AI extractions/month",
  "plans.features.ai3": "3 AI callsheets/month",
  "plans.features.ai60": "60 AI callsheets/month",
  "plans.features.ai400Annual": "400 AI callsheets per subscription year",
  "plans.features.customAI": "Custom AI extractions",
  "plans.features.stops10": "10 stops per trip",
  "plans.features.stops25": "25 stops per trip",
  "plans.features.unlimitedStops": "Unlimited stops",
  "plans.features.csvExport": "CSV export",
  "plans.features.allExports": "All exports",
  "plans.features.basicReports": "Basic reports",
  "plans.features.advancedReports": "Advanced reports",
  "plans.features.customReports": "Custom reports",
  "plans.features.calendarSync": "Calendar sync",
  "plans.features.noCalendarSync": "No calendar sync",
  "plans.features.routeTemplates": "Route templates",
  "plans.features.routeTemplates5": "5 route templates",
  "plans.features.routeTemplates50": "50 route templates",
  "plans.features.unlimitedTemplates": "Unlimited templates",
  "plans.features.costAnalysis": "Cost analysis",
  "plans.features.fullAnalytics": "Full analytics",
  "plans.features.prioritySupport": "Priority support",
  "plans.features.dedicatedSupport": "Dedicated support",
  "plans.features.sla": "Guaranteed SLA",
  "plans.features.customIntegrations": "Custom integrations",
  "plans.features.reports1": "1 saved report",
  "plans.features.reportsUnlimited": "Unlimited reports",
  "plans.features.callsheet1": "1 callsheet at a time",
  "plans.features.callsheetBulk20": "Up to 20 callsheets per upload",
  "plans.features.callsheetBatch3": "Up to 3 callsheets per upload",
  "plans.features.byoOpenrouter": "Your own OpenRouter key: choose the AI model (multimodal)",
  "plans.upgradeButton": "Upgrade to Pro",
  "plans.checkoutSuccess": "Payment completed. We are activating your Pro plan.",
  "plans.checkoutCancelled": "Payment was cancelled and no charge was made.",
  "plans.checkoutError": "Stripe secure checkout could not be opened.",
  "plans.portalError": "Subscription management could not be opened.",

  "tripDetail.title": "Trip details",
  "tripDetail.date": "Date",
  "tripDetail.project": "Project",
  "tripDetail.driver": "Driver",
  "tripDetail.currentUser": "Current user",
  "tripDetail.purpose": "Production company or client",
  "tripDetail.route": "Route",
  "tripDetail.totalDistance": "Total distance",
  "tripDetail.costBreakdown": "Cost breakdown",
  "tripDetail.customRate": "Custom rate",
  "tripDetail.invoicesTitle": "Fuel and maintenance invoices",
  "tripDetail.attach": "Attach",
  "tripDetail.attachInvoice": "Attach invoice",
  "tripDetail.invoiceAttachedTitle": "Invoice attached",
  "tripDetail.invoiceAttachedBody": "AI extraction will start automatically.",
  "tripDetail.onlyOneInvoice": "Attach only 1 invoice at a time.",
  "tripDetail.missingProjectIdForInvoice": "This trip has no associated project. Link it to a project so the invoice appears under Projects.",
  "tripDetail.invoiceExtracting": "Extracting with AI…",
  "tripDetail.invoiceCancelled": "Extraction cancelled",
  "tripDetail.totalDocumentedEmpty": "—",
  "tripDetail.invoiceStatusLabel": "Status",
  "tripDetail.totalDocumented": "Total documented: {amount}",
  "tripDetail.mapSetupTitle": "Set up Google Maps",
  "tripDetail.mapSetupBody": "To view the map, configure Google Maps via environment variables.",
  "tripDetail.saveToken": "Save",
  "tripDetail.mapNotConfiguredTitle": "Map not configured",
  "tripDetail.mapNotConfiguredBody": "Missing VITE_GOOGLE_MAPS_BROWSER_KEY (and in production GOOGLE_MAPS_SERVER_KEY on Vercel).",
  "tripDetail.mapLoadErrorTitle": "Couldn't load the map",
  "tripDetail.mapLoadError": "Couldn't load the route. Check your Google Maps configuration.",
  "tripDetail.mapNoRoute": "No route found for this trip.",
  "tripDetail.mapLoading": "Loading map…",
  "tripDetail.noDocuments": "No documents attached",
  "tripDetail.attachDocument": "Attach document",
  "tripDetail.tabMap": "Map",
  "tripDetail.tabDocument": "Document",
  "tripDetail.errorTitle": "Error",
  "tripDetail.authRequired": "You're not authenticated",
  "tripDetail.attachSaveFailed": "Couldn't save attachments",
  "tripDetail.attachSavedBody": "Attached {count} file(s).",
  "tripDetail.attachFailed": "Couldn't attach the file",
  "tripDetail.previewLoadFailed": "Couldn't load the preview.",
  "tripDetail.previewFrameTitle": "Preview",
  "tripDetail.previewUnavailable": "Preview isn't available in the browser.",
  "tripDetail.backToDocuments": "Documents",
  "tripDetail.openInNewTab": "Open in new tab",
  "tripDetail.downloadFile": "Download file",
  "tripDetail.driveFetchFailed": "Couldn't download from Drive.",

  "projectDetail.totalKm": "Total kilometers",
  "projectDetail.shootingDays": "Shooting days",
  "projectDetail.kmPerDay": "Km per shooting day",
  "projectDetail.co2Estimated": "CO₂ emissions (est.)",
  "projectDetail.callSheetsTitle": "Project call sheets",
  "projectDetail.uploadCallSheets": "Upload call sheets (up to 20)",
  "projectDetail.noCallSheets": "No call sheets uploaded for this project yet.",
  "projectDetail.invoicesTitle": "Project invoices",
  "projectDetail.attachInvoice": "Attach invoice",
  "projectDetail.confirmDeleteInvoice": "Delete this invoice? This will also delete the AI extraction result.",
  "projectDetail.toastProjectMismatch": "The project extracted by AI doesn't match, but the trip will be created anyway. Please review the result.",
  "projectDetail.toastMissingDate": "Couldn't extract the date. Please review manually.",
  "projectDetail.toastMissingLocations": "Couldn't extract locations. Please review manually.",
  "projectDetail.toastTripCreatedFromAi": "Trip created from project AI",
  "projectDetail.toastTripCreatedFromAiFailed": "Couldn't save the trip created by AI",
  "projectDetail.toastTripsCreatedFromAi": "Saved {count} trip(s) from AI.",
  "projectDetail.toastTripsCreatedFromAiFailed": "Couldn't save {count} trip(s) from AI.",
  "projectDetail.toastTripsCreatedFromAiPartial": "Saved {saved}, {failed} failed.",
  "projectDetail.toastTripCreatedFromAiError": "Error creating trip from extraction: {message}",
  "projectDetail.toastDocumentUploaded": "Document uploaded.",
  "projectDetail.toastOpenPdfError": "Error opening PDF: {message}",
  "projectDetail.toastStoragePathMissing": "File path not available.",
  "projectDetail.toastCallsheetDeleted": "Callsheet deleted",
  "projectDetail.toastDeleteError": "Error deleting: {message}",
  "projectDetail.toastReprocessingStarted": "Reprocessing started",
  "projectDetail.toastExtractionStarted": "Extraction started. The process will begin shortly.",
  "projectDetail.toastExtractionStartedShort": "Extraction started",
  "projectDetail.toastProcessingCancelled": "Processing cancelled when closing the project.",
  "projectDetail.toastExtractionStartError": "Error starting extraction: {message}",
  "projectDetail.toastInvalidSession": "Invalid session",
  "projectDetail.toastWorkerExecuted": "Worker executed: {count} jobs processed",
  "projectDetail.toastWorkerError": "Error running worker: {message}",
  "projectDetail.toastOpenDocumentError": "Error opening document: {message}",
  "projectDetail.toastInvoiceNoJob": "This invoice doesn't have an associated extraction job",
  "projectDetail.toastInvoiceDeleted": "Invoice deleted",
  "projectDetail.toastInvoiceLinkedToTrip": "This invoice belongs to a trip. Delete it from the associated trip.",
  "projectDetail.noInvoices": "No invoices uploaded for this project yet.",
  "projectDetail.discardAll": "Discard all",
  "projectDetail.saving": "Saving...",
  "projectDetail.saveTrips": "Save {count} trips",
  "trips.toastTripSaveFailedTitle": "Trip could not be saved",
  "trips.toastTripSaveFailedBody": "The change didn't reach the database. Check your connection or sign in again and retry.",
  "projectDetail.review": "Review",
  "projectDetail.cancelled": "Cancelled",
  "projectDetail.failed": "Error",
  "projectDetail.cancelProcessing": "Cancel processing",
  "projectDetail.tripLabel": "Trip",
  "projectDetail.outOfQuota": "Limit reached",
  "projectDetail.reprocess": "Reprocess",
  "projectDetail.reprocessWithAi": "Reprocess with AI",
  "projectDetail.extractDataWithAi": "Extract data with AI",
  "projectDetail.processing": "Processing...",
  "projectDetail.toastDuplicateTripSkipped": "A trip with the same date and route already exists; no duplicate was created.",
  "bulk.duplicateTripConfirm": "A trip with the same date and route already exists. Save anyway?",
  "projectDetail.toastAlreadyProcessing": "This document is already being processed; its status will update when it finishes. If it gets stuck, try again in a couple of minutes.",
  "projectDetail.processNow": "Process now",

  // Project expenses
  "projectExpenses.title": "Project expenses",
  "projectExpenses.description": "Receipts not associated with a specific trip. Scan or upload receipts using camera or drag and drop.",
  "projectExpenses.total": "Total",
  "projectExpenses.saveError": "Error saving expense",
  "expenses.receipt": "receipt",
  "expenses.receipts": "receipts",

  "vehicleConfig.title": "Vehicle & cost parameters",
  "vehicleConfig.subtitle": "Configure your vehicle to estimate costs without invoices.",
  "vehicleConfig.vehicleType": "Vehicle type",
  "vehicleConfig.fuelConsumption": "Fuel consumption",
  "vehicleConfig.fuelPrice": "Fuel price",
  "vehicleConfig.evConsumption": "Consumption (kWh/100km)",
  "vehicleConfig.energyPrice": "Energy price (€/kWh)",
  "vehicleConfig.maintenancePerKm": "Maintenance (€/km)",
  "vehicleConfig.otherPerKm": "Other (€/km)",
  "vehicleConfig.estimatedFuelPerKm": "Estimated energy (€/km)",
  "vehicleConfig.typeUnknown": "Not specified",
  "vehicleConfig.typeGasoline": "Gasoline",
  "vehicleConfig.typeDiesel": "Diesel",
  "vehicleConfig.typeEv": "Electric",
  "vehicleConfig.cancel": "Cancel",
  "vehicleConfig.save": "Save",
  "vehicleConfig.saving": "Saving…",
  "vehicleConfig.toastSaving": "Saving configuration…",
  "vehicleConfig.toastSaved": "Configuration saved",
  "vehicleConfig.invalidNumber": "Invalid number",

  "reports.verifyTitle": "Generate tax report",
  "reports.verifyChecklistTitle": "Pre-export checklist",
  "reports.verifyChecklistBody": "The system reviewed the trips for the selected period to find potential issues.",
  "reports.verifyNoProblems": "No potential issues found. Ready to generate.",
  "reports.verifyFoundWarnings": "{count} warning(s) found",
  "reports.verifyContinueOrReview": "You can continue generating the report or review the trips first.",
  "reports.back": "Back",
  "reports.generateReport": "Generate report",
  "reports.title": "Reports",
  "reports.subtitle": "Generate and manage mileage reports",
  "reports.generateNew": "Generate new report",
  "reports.project": "Project",
  "reports.selectProject": "Select project",
  "reports.allProjects": "All projects",
  "reports.month": "Month",
  "reports.year": "Year",
  "reports.from": "From",
  "reports.to": "To",
  "reports.toastInvalidRangeTitle": "Invalid date range",
  "reports.toastInvalidRangeBody": "The 'From' date cannot be after 'To'.",
  "reports.tableGeneratedAt": "Generated on",
  "reports.tablePeriod": "Report period",
  "reports.tableActions": "Actions",
  "reports.view": "View",
  "reports.warningImprobableDistance": "Improbable distance",
  "reports.warningZeroDistance": "Distance is 0 (or invalid)",
  "reports.warningMissingPurpose": "Missing trip purpose",
  "reports.toastGeneratedTitle": "Report generated",
  "reports.toastGeneratedBody": "The tax report was generated successfully.",

  "reportView.filePrefix": "report",
  "reportView.sheetName": "Report",
  "reportView.toastExportingTitle": "Exporting to {format}",
  "reportView.toastExportingBody": "Your file will download shortly.",
  "reportView.toastPdfDownloadedTitle": "PDF downloaded",
  "reportView.toastPdfDownloadedBody": "The file was downloaded successfully.",
  "reportView.toastExportErrorTitle": "Export error",
  "reportView.toastExportErrorBody": "Could not generate the file. Please try again.",
  "reportView.backToReports": "Back to reports",
  "reportView.backShort": "Back",
  "reportView.export": "Export",
  "reportView.exportExcel": "Excel (.xlsx)",
  "reportView.exportPdf": "PDF",
  "reportView.exportCsv": "CSV",
  "reportView.exportZip": "Download ZIP with documentation",
  "reportView.exportZipPreparing": "Preparing documents...",
  "reportView.exportZipDone": "ZIP downloaded",
  "reportView.exportZipEmpty": "No documents associated with this report",
  "reportView.exportZipError": "Error generating ZIP",
  "reportView.exportZipProOnly": "Pro plan only",
  "reportView.print": "Print",
  "reportView.saveReport": "Save",
  "reportView.toastSaveErrorTitle": "Could not save the report",
  "reportView.toastSaveErrorBody": "The report is still open. Check your connection and try saving again.",
  "reportView.toastSavedTitle": "Report saved",
  "reportView.toastSavedBody": "The report was saved to the list.",
  "reportView.notFoundTitle": "Report not found",
  "reportView.notFoundBody": "This report doesn't exist or was deleted.",
  "reportView.reportTitle": "Trip Log",
  "reportView.periodLabel": "Period",
  "reportView.driverLabel": "Driver",
  "reportView.addressLabel": "Address",
  "reportView.licensePlateLabel": "License plate(s)",
  "reportView.projectLabel": "Project",
  "reportView.ratePerKmLabel": "Rate per km",
  "reportView.passengerSurchargeLabel": "Passenger surcharge",
  "reportView.mergePassengers": "Merge passenger surcharge into mileage",
  "reportView.colReimbursement": "Reimbursement",
  "reportView.totalReimbursementLabel": "Total reimbursement",
  "reportView.colDate": "Date",
  "reportView.colProject": "Project",
  "reportView.colCompanyProducer": "Company/Producer",
  "reportView.colRoute": "Route",
  "reportView.colPassengers": "Passengers",
  "reportView.colPassengersShort": "Pass.",
  "reportView.colDistance": "Distance",
  "reportView.colDistanceKm": "Distance (km)",
  "reportView.totalDistanceLabel": "Total distance traveled",
  "reportView.totalShort": "Total",
  "reportView.pdfLanguage": "PDF language",

  "dashboard.actionUploadCallsheet": "Upload call sheet",
  "dashboard.actionAddTrip": "Add trip",
  "dashboard.kpiBillableMonth": "€ billable (month)",
  "dashboard.kpiKmMonth": "Kilometers (month)",
  "dashboard.kpiTripsMonth": "Trips (month)",
  "dashboard.kpiCo2Month": "CO₂ (month)",
  "dashboard.vsPrevMonth": "vs. previous month",
  "dashboard.treePerYearShort": "Annual uptake of approximately {trees} mature tree",
  "dashboard.treesPerYearShort": "Annual uptake of approximately {trees} mature trees",
  "dashboard.attentionUnavailable": "Could not refresh alerts. Please try again.",
  "dashboard.attentionTitle": "Needs your attention",
  "dashboard.attentionEmpty": "All good",
  "dashboard.attentionCallsheetFailed": "Call sheet failed",
  "dashboard.attentionCallsheetReview": "Call sheet needs review",
  "dashboard.attentionMore": "+{count} more in Trips",
  "dashboard.aiCounterTitle": "AI call sheets",
  "dashboard.aiCounterRenews": "Renews on {month} 1st",
  "dashboard.aiCounterExhausted": "Monthly limit reached",
  "dashboard.aiCounterNearLimit": "Running low this month",
  "dashboard.reportReadyTitle": "{month} report ready",
  "dashboard.reportReadyCta": "Generate PDF",
  "dashboard.carMarginTitle": "Your car's net margin",
  "dashboard.carMarginMonth": "Your car netted you ~{amount} this month",
  "dashboard.carMarginDetail": "Billed {billed} · estimated cost {cost}",
  "dashboard.carMarginPerKmDetail": "{rate} Kilometergeld − {cost} real cost per km",
  "dashboard.aiChipLabel": "AI callsheets",
  "dashboard.aiChipAnnual": "AI callsheets/year",
  "dashboard.aiChipMonthly": "AI callsheets/month",
  "dashboard.reportReadyDesc": "{count} trips recorded. Generate the PDF and send it to production.",
  "dashboard.carMarginMissing": "Add consumption, fuel price and per-km costs in Settings to calculate this.",
  "dashboard.proUsageTitle": "% professional use",
  "dashboard.proUsageDetail": "{workKm} professional km of {totalKm} total km ({year})",
  "dashboard.proUsageMissing": "Enter your car's annual total kilometers in Settings (from a garage invoice or inspection).",
  "dashboard.monthlyBarsTitle": "Km & € per month",
  "settings.annualCarTotalKm": "Car's total km this year",
  "settings.annualCarTotalKmHint": "Odometer reading or garage/inspection invoice; used for the % of professional use.",
  "settings.carCostsTitle": "Car costs",
  "settings.carCostsHint": "Used for the \"Your car's net margin\" card on the dashboard.",
  "settings.fuelPricePerLiter": "Fuel price (€/L)",
  "settings.electricityPricePerKwh": "Electricity price (€/kWh)",
  "settings.maintenanceEurPerKm": "Maintenance (€/km)",
  "settings.otherEurPerKm": "Other costs (€/km)",
  "bulk.aiQuotaLine": "AI call sheets this month: {used}/{limit}",
  "bulk.aiQuotaAnnualLine": "AI callsheets this subscription year: {used}/{limit}",

  "reportPdf.title": "Mileage reimbursement statement",
  "reportPdf.colPurpose": "Purpose",
  "reportPdf.colRate": "€/km",
  "reportPdf.colAmount": "Amount",
  "reportPdf.tripsLabel": "Trips",
  "reportPdf.totalKmLabel": "Total kilometers",
  "reportPdf.travelCosts": "Mileage (Kilometergeld)",
  "reportPdf.expensesTitle": "Expenses",
  "reportPdf.expenseToll": "Toll",
  "reportPdf.expenseParking": "Parking",
  "reportPdf.expenseFuel": "Fuel",
  "reportPdf.expenseOther": "Other",
  "reportPdf.receiptsAnnex": "Receipts attached (ZIP export).",
  "reportPdf.grandTotal": "Total amount",
  "reportPdf.inclPassengerSurcharge": "incl. passenger surcharge",
  "reportPdf.inclExpenses": "incl. expenses",
  "reportPdf.totalBoxLabel": "Total",
  "reportPdf.shootDayOne": "1 shoot day",
  "reportPdf.shootDays": "{count} shoot days",
  "reportPdf.co2Line": "CO2 emissions: {kg} kg — equal to the yearly uptake of ~{trees} trees.",
  "reportPdf.co2Only": "CO2 emissions: {kg} kg.",
  "reportPdf.colCo2": "CO2 (kg)",
  "reportPdf.totalCo2Label": "Total CO2",
  "dashboard.setupTitle": "Complete your profile so everything calculates itself",
  "dashboard.setupBothMissing": "Your rate per km and base address are missing: without them we can't calculate distances or amounts.",
  "dashboard.setupRateMissing": "Your rate per km is missing: without it we can't calculate your trip amounts.",
  "dashboard.setupBaseMissing": "Your base address is missing: with it, origin and distance fill in automatically when creating a trip.",
  "dashboard.setupCta": "Complete in Settings",
  "reportView.tripsCount": "{count} trips",
  "reportView.tripsCountOne": "1 trip",
  "reportView.contentMenu": "Report contents",
  "reportView.optCo2": "CO2 column",
  "reportView.optTrees": "Equivalent trees",
  "reportView.optSource": "Factor sources",
  "reportView.optSignature": "Signature block",
  "reportPdf.co2Source": "Source: Umweltbundesamt/JEC (tank-to-wheel); electricity: Ember/OWID {year}; ~{treeKg} kg CO2 per tree per year.",
  "reportPdf.signature": "Date, signature",
  "reportPdf.footer": "Created with Fahrtenbuch Pro · fahrtenbuchpro.com",
  "reportPdf.pageOf": "Page {page} of {pages}",

  "advancedRoutes.createTemplateTitle": "Create new template",
  "advancedRoutes.saveAsTemplate": "Save as template",
  "advancedRoutes.createTemplateDesc": "Save this route for future trips.",
  "advancedRoutes.save": "Save",
  "advancedRoutes.editTemplateTitle": "Edit template",
  "advancedRoutes.basicInfo": "Basic information",
  "advancedRoutes.templateName": "Template name",
  "advancedRoutes.category": "Category",
  "advancedRoutes.categoryBusiness": "Business",
  "advancedRoutes.categoryCommute": "Commute",
  "advancedRoutes.categoryClient": "Client",
  "advancedRoutes.categoryOther": "Other",
  "advancedRoutes.routeDetails": "Route details",
  "advancedRoutes.startLocation": "Start location",
  "advancedRoutes.endLocation": "Destination",
  "advancedRoutes.distanceKm": "Distance (km)",
  "advancedRoutes.estimatedTimeMin": "Estimated time (min)",
  "advancedRoutes.additionalDetails": "Additional details",
  "advancedRoutes.description": "Description",
  "advancedRoutes.descriptionPlaceholder": "Enter a description",
  "advancedRoutes.cancel": "Cancel",
  "advancedRoutes.createTemplate": "Create template",
  "advancedRoutes.saveChanges": "Save changes",
  "advancedRoutes.pageTitle": "Route templates",
  "advancedRoutes.pageSubtitle": "Save frequent routes as reusable templates.",
  "advancedRoutes.statTotalTemplates": "Total templates",
  "advancedRoutes.statAvgDistance": "Average distance",
  "advancedRoutes.statMostUsed": "Most used",
  "advancedRoutes.none": "None",
  "advancedRoutes.categoryAll": "All categories",
  "advancedRoutes.toastLoadError": "Couldn't load templates",
  "advancedRoutes.toastLoginRequired": "Sign in to save templates",
  "advancedRoutes.toastNameRequired": "Add a name to the template",
  "advancedRoutes.errorUpdateFailed": "Couldn't update the template",
  "advancedRoutes.errorCreateFailed": "Couldn't create the template",
  "advancedRoutes.toastUpdated": "Template updated",
  "advancedRoutes.toastCreated": "Template created",
  "advancedRoutes.edit": "Edit",
  "advancedRoutes.use": "Use",
  "advancedRoutes.loading": "Loading…",
  "advancedRoutes.noTemplatesInCategory": "No templates in this category. Create your first template to get started.",
  "advancedRoutes.toastSaveFailed": "Couldn't save the template",
  "advancedRoutes.toastUsesUpdateFailed": "Couldn't update the counter",

  "advancedCosts.pageTitle": "Cost analysis",
  "advancedCosts.pageSubtitle": "Personal cost analysis based on your vehicle configuration.",
  "advancedCosts.uploadInvoice": "Upload invoice",
  "advancedCosts.uploadShort": "Upload",
  "advancedCosts.periodAllTime": "All time",
  "advancedCosts.periodLastMonth": "Last month",
  "advancedCosts.periodLast3Months": "Last 3 months",
  "advancedCosts.periodLast6Months": "Last 6 months",
  "advancedCosts.periodLastYear": "Last year",
  "advancedCosts.tabSummary": "Summary",
  "advancedCosts.tabMonthly": "Monthly costs",
  "advancedCosts.monthlyChartTitle": "Monthly trend",
  "advancedCosts.statTotalDistance": "Total distance",
  "advancedCosts.statTotalTrips": "Total trips",
  "advancedCosts.statEstimatedTotalCost": "Estimated total cost",
  "advancedCosts.statAvgCostPerKm": "Avg. cost / km",
  "advancedCosts.breakdownTitle": "Basic cost breakdown",
  "advancedCosts.breakdownFuelEnergy": "Fuel / Energy",
  "advancedCosts.breakdownTolls": "Tolls",
  "advancedCosts.breakdownParking": "Parking",
  "advancedCosts.breakdownOther": "Other",
  "advancedCosts.breakdownAvgPerTrip": "Avg. cost / trip",
  "advancedCosts.assumptionsTitle": "Cost summary",
  "advancedCosts.assumptionFuelPerKm": "Fuel: {amount}/km",
  "advancedCosts.assumptionTollsTotal": "Tolls (total): {amount}",
  "advancedCosts.assumptionParkingTotal": "Parking (total): {amount}",
  "advancedCosts.assumptionOtherTotal": "Other (total): {amount}",
  "advancedCosts.assumptionsHint": "These costs are derived from your vehicle configuration and trip expenses.",
  "advancedCosts.warningNoVehicleConfig": "Incomplete vehicle configuration",
  "advancedCosts.warningNoVehicleConfigDetails": "You haven't completely set up your vehicle information in your profile. Fuel/energy costs cannot be calculated without this data.",
  "advancedCosts.assumptionsEdit": "Click here to edit.",
  "advancedCosts.projectAnalysisTitle": "Cost analysis by project",
  "advancedCosts.projectLabel": "Project:",
  "advancedCosts.allProjects": "All projects",
  "advancedCosts.tableProject": "Project",
  "advancedCosts.tableDistanceAbbr": "Dist.",
  "advancedCosts.tableTrips": "Trips",
  "advancedCosts.tableTotal": "Total",
  "advancedCosts.tableEurPerKm": "€/km",
  "advancedCosts.monthlySummaryTitle": "Monthly summary",
  "advancedCosts.tableMonth": "Month",
  "advancedCosts.tableDistance": "Distance",
  "advancedCosts.tableFuelEnergy": "Fuel / Energy",
  "advancedCosts.tableTolls": "Tolls",
  "advancedCosts.tableParking": "Parking",
  "advancedCosts.tableOther": "Other",
  "advancedCosts.toastSelectProject": "Select a project to attach the invoice",
  "advancedCosts.toastSupabaseNotConfigured": "Supabase is not configured",
  "advancedCosts.toastMaxDocuments": "Maximum 20 documents at a time",
  "advancedCosts.toastOnlyPdfOrImages": "Only PDF or image files are allowed",
  "advancedCosts.toastNotAuthenticated": "You're not authenticated",
  "advancedCosts.toastUploadedInvoices": "Uploaded {count} invoice(s)/document(s). AI extraction will start automatically.",
  "advancedCosts.toastFailedDocuments": "Failed {count} document(s)",
  "advancedCosts.errorUploadInvoice": "Error uploading invoice",
  "advancedCosts.chartNoData": "No data to display.",
  "advancedCosts.chartNoCostsTitle": "No cost amounts found for this period yet.",
  "advancedCosts.chartNoCostsBody": "Configure your vehicle or upload invoices with amounts (or run extraction) to see charts.",
  "advancedCosts.chartSeriesTotal": "Total",
  "advancedCosts.pageSubtitleNew": "Analyze the real profitability of your trips by comparing costs vs reimbursements.",
  "advancedCosts.periodThisMonth": "This month",
  "advancedCosts.periodLastQuarter": "Last quarter",
  "advancedCosts.periodThisYear": "This year",
  "advancedCosts.reimbursement": "Reimbursement",
  "advancedCosts.realCost": "Real cost",
  "advancedCosts.balance": "Balance",
  "advancedCosts.profitable": "Profitable",
  "advancedCosts.notProfitable": "Not profitable",
  "advancedCosts.tripsLabel": "trips",
  "advancedCosts.trip": "trip",
  "advancedCosts.costBreakdown": "Cost breakdown",
  "advancedCosts.projectBreakdown": "Profitability by project",
  "advancedCosts.noProjectsInPeriod": "No projects with trips in this period.",
  "advancedCosts.tableCost": "Cost",
  "advancedCosts.tableReimbursement": "Reimbursement",
  "advancedCosts.total": "Total",
  "advancedCosts.infoFooter": "Costs are calculated based on your vehicle configuration and expenses recorded for each trip.",
  "advancedCosts.loadMoreProjects": "Load more projects",
  "advancedCosts.loadMoreTrips": "Load more trips",
  "advancedCosts.remaining": "remaining",

  "advancedEmissions.configTitle": "CO₂ analysis settings",
  "advancedEmissions.configSubtitle": "Set your CO₂ emissions analysis preferences",
  "advancedEmissions.pageTitle": "CO₂ ranking",
  "advancedEmissions.pageSubtitle": "Evaluate the environmental impact of your trips and compare them.",
  "advancedEmissions.austriaNotice": "CO₂ emission factors used (Climatiq UBA Austria 2022, 0.2472 kg CO₂e/km) are specific to Austria. If your country is different, the values shown are an approximation based on Austrian data.",
  "advancedEmissions.export": "Export",
  "advancedEmissions.welcomeTitle": "Welcome to CO₂ analysis",
  "advancedEmissions.welcomeBody": "Get insights into the environmental impact of your trips. Configure your analysis preferences to start tracking your carbon footprint and discover more sustainable travel opportunities.",
  "advancedEmissions.configureButton": "Configure CO₂ analysis",
  "advancedEmissions.totalEmissions": "Total CO₂ emissions",
  "advancedEmissions.totalFootprintTitle": "Total carbon footprint",
  "advancedEmissions.totalFootprintUnit": "kg CO₂",
  "advancedEmissions.compensationTitle": "Illustrative equivalent",
  "advancedEmissions.compensationBody": "Approximate uptake over one year:",
  "advancedEmissions.treeSingular": "tree",
  "advancedEmissions.treePlural": "trees",
  "advancedEmissions.breakdownTitle": "Project breakdown",
  "advancedEmissions.breakdownTitleTrips": "Trip breakdown",
  "advancedEmissions.trendChartTitle": "Emissions trend (kg CO₂)",
  "advancedEmissions.trendChartProjected": "Projected",
  "advancedEmissions.trendChartReal": "Real",
  "advancedEmissions.trendChartWeek": "Week {week}",
  "advancedEmissions.trendChartNoData": "No data to display.",
  "advancedEmissions.co2TotalLabel": "TOTAL CO₂",
  "advancedEmissions.co2PerPassengerLabel": "PER PASSENGER",
  "advancedEmissions.tripsLabel": "trips",
  "advancedEmissions.avgEfficiency": "Average efficiency",
  "advancedEmissions.fuelConsumption": "Fuel consumption",
  "advancedEmissions.electricConsumption": "Electric consumption",
  "advancedEmissions.treesNeeded": "Trees needed",
  "advancedEmissions.resultsTitle": "CO₂ ranking results ({count})",
  "advancedEmissions.noResultsTitle": "No results for this period",
  "advancedEmissions.noResultsBody": "Change the time range or add trips to see the ranking.",
  "advancedEmissions.metricCo2Kg": "CO₂ (kg)",
  "advancedEmissions.metricEfficiency": "Efficiency (kg/km)",
  "advancedEmissions.metricDistance": "Distance (km)",
  "advancedEmissions.metricTrips": "Trips",
  "advancedEmissions.viewMode": "View mode",
  "advancedEmissions.viewModeProjectsOnly": "Projects only",
  "advancedEmissions.viewModeAllTrips": "All trips",
  "advancedEmissions.sortBy": "Sort by",
  "advancedEmissions.sortByCo2": "CO₂ (kg)",
  "advancedEmissions.sortByEfficiency": "Efficiency",
  "advancedEmissions.sortByDistance": "Distance",
  "advancedEmissions.sortByHelp": "Select the main metric to rank projects",
  "advancedEmissions.timeRange": "Time range",
  "advancedEmissions.allTime": "All time",
  "advancedEmissions.last7Days": "Last 7 days",
  "advancedEmissions.last30Days": "Last 30 days",
  "advancedEmissions.last90Days": "Last 90 days",
  "advancedEmissions.last6Months": "Last 6 months",
  "advancedEmissions.thisYear": "This year",
  "advancedEmissions.selectPeriodHelp": "Select the time period for CO₂ analysis",
  "advancedEmissions.searchPlaceholder": "Search project...",
  "advancedEmissions.resetFilters": "Reset",
  "advancedEmissions.noFilterResultsTitle": "No results",
  "advancedEmissions.noFilterResultsBody": "No results match the current filters.",
  "advancedEmissions.filterByProjectPlaceholder": "Filter by project...",
  "advancedEmissions.sortDirectionAsc": "Ascending order",
  "advancedEmissions.sortDirectionDesc": "Descending order",
  "advancedEmissions.fuelEfficiency": "Fuel efficiency (L/100km)",
  "advancedEmissions.fuelEfficiencyHelp": "Average fuel consumption per 100km (used for CO₂ calculations)",
  "advancedEmissions.aboutTitle": "About CO₂ calculations",
  "advancedEmissions.aboutBody": "CO₂ emissions are calculated based on fuel efficiency and vehicle type. This analysis helps you understand the environmental impact of your trips and identify opportunities to travel more efficiently.",
  "advancedEmissions.cancel": "Cancel",
  "advancedEmissions.save": "Save settings",
  "advancedEmissions.fallbackTripName": "Trip",
  "advancedEmissions.fallbackProjectName": "Project",
  "advancedEmissions.ratingVeryLow": "Very low",
  "advancedEmissions.ratingLow": "Low",
  "advancedEmissions.ratingModerate": "Moderate",
  "advancedEmissions.ratingHigh": "High",
  "advancedEmissions.ratingVeryHigh": "Very high",
  "advancedEmissions.trendStable": "Stable",
  "advancedEmissions.trendNew": "New",
  "advancedEmissions.trendImproving": "Improving",
  "advancedEmissions.trendWorsening": "Worsening",
  "advancedEmissions.allProjects": "All projects",
  "validation.title": "Validation",
  "validation.excessive": "Excessive consumption: {value} {unit}. Maximum allowed is {max} {unit}.",
  "validation.tooLow": "Consumption too low: {value} {unit}. Realistic minimum is {min} {unit}.",

  "settings.title": "Settings",
  "settings.tabProfile": "Profile",
  "settings.tabApis": "APIs & external services",
  "settings.tabPersonalization": "Personalization",
  "settings.tabLanguage": "Language",
  "settings.tabNews": "News",
  "settings.tabHelp": "Help & docs",
  "settings.cancel": "Cancel",
  "settings.save": "Save settings",
  "settings.refresh": "Refresh",
  "settings.toastSaving": "Saving settings…",
  "settings.toastSaved": "Settings saved",
  "settings.sessionInvalid": "Invalid session.",
  "settings.deleteAccountFailed": "Couldn't delete the account.",
  "settings.accountDeleted": "Account deleted.",
  "settings.googleConnectFailed": "Couldn't start the connection",
  "settings.googleDisconnectFailed": "Couldn't disconnect",
  "settings.profileSectionTitle": "My profile",
  "settings.fullName": "Full name",
  "settings.vatId": "VAT ID",
  "settings.licensePlate": "License plate",
  "settings.ratePerKm": "Rate (€/km)",
  "settings.passengerSurcharge": "Passenger surcharge (€)",
  "settings.ratesSource": "Rate set in Settings. Applies to all trips and projects.",
  "settings.ratesDefaults": "Empty fields use €0.50/km and €0.15 per passenger. An explicit zero is respected.",
  "settings.baseAddress": "Base address",
  "settings.city": "City",
  "settings.country": "Country",
  "settings.vehicleSectionTitle": "Vehicle & emissions",
  "settings.vehicleSectionBody": "Set fuel type and consumption to calculate CO₂ correctly.",
  "settings.fuelType": "Vehicle type",
  "settings.fuelTypeUnknown": "Not specified",
  "settings.fuelTypeGasoline": "Gasoline",
  "settings.fuelTypeDiesel": "Diesel",
  "settings.fuelTypeEv": "Electric",
  "settings.fuelConsumption": "Consumption (L/100km)",
  "settings.evConsumption": "Consumption (kWh/100km)",
  "settings.gridFactor": "Grid intensity (AT) (kg CO₂/kWh)",
  "settings.gridFactorHelp": "Annual average for your country's grid (AT/DE/CZ/HU), source Ember/Our World in Data. Reviewed once a year.",
  "settings.emissionsGridAnnualNote": "* Annual average of the country's electricity generation; updated manually once a year.",
  "settings.emissionsStaticFuelTitle": "Fuel factor (static table)",
  "settings.emissionsStaticGridTitle": "Electricity grid (annual average)",
  "settings.emissionsDataSourceTitle": "Emissions Data Source",
  "settings.emissionsDataSourceBody": "CO₂ calculations use static factors with cited sources; precision comes from your vehicle's real consumption:",
  "settings.emissionsFactor": "Factor",
  "settings.emissionsSource": "Source",
  "settings.emissionsYear": "Year",
  "settings.emissionsRegion": "Region",
  "settings.emissionsActivityId": "Activity ID",
  "settings.emissionsWellToWheel": "* Tank-to-wheel methodology: CO₂ emitted when burning the fuel (excludes refining and transport).",
  "settings.emissionsIntensity": "Intensity",
  "settings.emissionsZone": "Zone",
  "settings.emissionsUpdated": "Updated",
  "settings.deleteAccountTitle": "Delete account",
  "settings.deleteAccountBody": "Your trips, documents, projects, reports and settings will be permanently deleted. Any subscription will be cancelled to prevent renewals. This may take a few minutes. If interrupted, press again to continue; data already deleted cannot be recovered.",
  "settings.deletingAccount": "Deletion in progress. Waiting for pending operations and removing data. Keep this window open.",
  "settings.deleteAccountIncomplete": "Deletion has not completed. Your account remains pending deletion; press Delete my account again to continue. New changes cannot be saved meanwhile.",
  "settings.deleteAccountButton": "Delete my account",
  "settings.apisGeminiBody": "Server-side AI for callsheet parsing",
  "settings.apisInactive": "Inactive",
  "settings.apisSavedSelection": "Gemini status reflects the saved settings. Save changes to apply them.",
  "ui.aiProviderUnavailable": "The selected AI provider could not be used. Check your saved settings and plan. No other provider was used.",
  "settings.apisActive": "Active",
  "settings.apisOpenRouterTitle": "OpenRouter (Optional)",
  "settings.apisOpenRouterBody": "Use your own API key",
  "settings.apisApiKey": "API key",
  "settings.apisGoogleCalendarBody": "Sync events",
  "settings.apisConnect": "Connect",
  "settings.appearanceTitle": "Appearance settings",
  "settings.theme": "Theme",
  "settings.current": "current",
  "settings.themeLight": "Light",
  "settings.themeLightDesc": "Light backgrounds and a subtle glass effect.",
  "settings.themeDark": "Dark",
  "settings.themeDarkDesc": "Higher contrast for low-light environments.",
  "settings.themeHint": "Switch between light and dark without changing the rest of the styles.",
  "settings.backgroundImage": "Background image",
  "settings.uploadFromComputer": "Upload from computer",
  "settings.remove": "Remove",
  "settings.none": "None",
  "settings.presetBackgrounds": "Preset backgrounds",
  "settings.uiOpacity": "UI opacity",
  "settings.uiBlur": "UI blur",
  "settings.backgroundBlur": "Background blur",
  "settings.appVersionTitle": "App version",
  "settings.checkUpdates": "Check for updates",
  "settings.changelogBody": "See recent changes and updates",
  "settings.viewChangelog": "View changelog",
  "settings.helpTitle": "Help & documentation",
  "settings.docsTitle": "Documentation",
  "settings.docsBody": "Learn how to use all features",
  "settings.viewDocs": "View documentation",
  "settings.supportTitle": "Support",
  "settings.supportBody": "Need help? Contact us",
  "settings.contactSupport": "Contact support",
  "settings.tourTitle": "Interactive tour",
  "settings.tourBody": "Replay the guided tour of the app",
  "settings.tourStart": "Start tour",
  "settings.build": "Build:",

  // Interactive tour (OnboardingTour)
  "tour.stepOf": "Step {current} of {total}",
  "tour.skip": "Skip tour",
  "tour.back": "Back",
  "tour.next": "Next",
  "tour.finish": "Finish",
  "tour.welcomeTitle": "Welcome to Fahrtenbuch Pro",
  "tour.welcomeBody": "We'll take you on a guided walk through the app: the dashboard, trips, projects, reports and the calendar. One minute and you're set.",
  "tour.kpisTitle": "Your month at a glance",
  "tour.kpisBody": "€ to invoice, kilometres, trips and the month's CO₂, compared with the previous month. Each card takes you to its page.",
  "tour.attentionTitle": "AI quota and alerts",
  "tour.attentionBody": "The counter shows the callsheet extractions you've used this month. The bell gathers what needs your attention: callsheets to review and trips with odd data.",
  "tour.addTripTitle": "Add trips by hand",
  "tour.addTripBody": "With \"Add trip\": the origin starts from your base address, the distance is calculated automatically with Google Maps, and you can add up to 25 stops, passengers and expenses with receipts.",
  "tour.bulkTitle": "Or let the AI create them",
  "tour.bulkBody": "With \"Bulk upload\" you upload callsheets as PDF or photo and the AI extracts the date, the project and the shooting locations. You review and save.",
  "tour.tableTitle": "Your trips table",
  "tour.tableBody": "Filter by project and year, multi-select for bulk deleting, and use each row's ⋮ menu: view map, add to calendar, edit or delete.",
  "tour.projectsTitle": "Projects",
  "tour.projectsBody": "Each production is a folder: it groups its trips, callsheets and documents, and passes down the production company and the rate.",
  "tour.reportsTitle": "Reports",
  "tour.reportsBody": "This is where you generate the Kilometergeld report: pick a project and period and export it as PDF (German, English or Spanish), CSV or a ZIP with receipts.",
  "tour.calendarTitle": "Calendar, both ways",
  "tour.calendarBody": "Connect Google Calendar: turn events into trips in one click, and likewise send trips to the calendar from the Trips table.",
  "tour.settingsTitle": "Start with your profile",
  "tour.settingsBody": "In Settings → Profile set your rate per km and your base address: without them there are no amounts and no automatic distance.",
  "tour.planTitle": "Your plan and AI quota",
  "tour.planBody": "See your current plan here. Pro raises the monthly callsheet extractions and lets you use your own OpenRouter key.",
  "tour.finalTitle": "All set!",
  "tour.finalBody": "Whenever you need a refresher, the documentation and this tour live in Settings → Help & docs. Happy shooting!",

  // Add/edit trip modal tutorial (TripModalTour)
  "tripTour.help": "Form tutorial",
  "tripTour.routeTitle": "The trip route",
  "tripTour.routeBody": "The origin starts from your base address: leave it empty and the grey address is used. Type the destination and pick a suggestion; \"Add stop\" gives you up to 25 stops, draggable to reorder.",
  "tripTour.originModeTitle": "Special origin: chain your day",
  "tripTour.originModeBody": "\"Start from my base address\" is the normal trip from home. \"Continue from last destination\" fills the origin with your previous trip's destination — ideal for the day's second trip. \"Return to my base address\" locks the destination to your base for the ride home.",
  "tripTour.distanceTitle": "Distance and rate",
  "tripTour.distanceBody": "The distance is calculated automatically with Google Maps once the route is complete; correct it by hand or recalculate with the middle button. The rate is optional: if set, it overrides the project's and the profile's.",
  "tripTour.passengersTitle": "Passengers",
  "tripTour.passengersBody": "Enter how many colleagues rode along: their per-km surcharge is calculated separately and ALWAYS stays apart from the mileage, in the report too.",
  "tripTour.expensesTitle": "Expenses with receipts",
  "tripTour.expensesBody": "Toll, parking, fuel and others: type the amount in EUR and attach the receipt photo with the camera button. They become their own lines in the report.",
  "tripDetail.invoiceFailed": "Error",
  "tripDetail.invoiceNeedsReview": "Review",

  // Expense Scan
  "tripModal.expenses": "Expenses",
  "tripModal.expensesHintBefore": "Use",
  "tripModal.expensesHintAfter": "to attach a photo of the receipt and enter the amount manually in EUR. Foreign receipts (CZK/HUF): enter the amount already converted to EUR.",
  "expenseScan.title": "Attach receipt",
  "expenseScan.scanReceipt": "Attach receipt",
  "expenseScan.typeToll": "Toll",
  "expenseScan.typeParking": "Parking",
  "expenseScan.typeFuel": "Fuel",
  "expenseScan.typeOther": "Other expense",
  "expenseScan.processing": "Saving receipt...",
  "expenseScan.error": "Error extracting data",
  "expenseScan.dataExtracted": "Data extracted",
  "expenseScan.amount": "Amount",
  "expenseScan.quantity": "Quantity",
  "expenseScan.pricePerUnit": "Price/unit",
  "expenseScan.vendor": "Vendor",
  "expenseScan.date": "Date",
  "expenseScan.cancel": "Cancel",
  "expenseScan.extract": "Extract data",
  "expenseScan.attach": "Attach receipt",
  "expenseScan.receiptAttached": "Receipt attached",
  "expenseScan.retry": "Retry",
  "expenseScan.useData": "Use data",
  "expenseScan.invalidFileType": "Only images allowed (JPG, PNG, WebP)",
  "expenseScan.fileTooLarge": "Image too large (max 10MB)",
  "expenseScan.extracted": "Data extracted successfully",
  "expenseScan.canEditAfter": "You can edit after confirming",
  "expenseScan.rotateImage": "Rotate image",
  "expenseScan.noDataExtracted": "Could not extract amount. Make sure the receipt is readable.",
  "expenseScan.uploadError": "Error uploading image",
  "expenseScan.dragDropText": "Drag and drop image here",
  "expenseScan.clickToUpload": "or click to select",
  "expenseScan.useCamera": "Use camera",
  "expenseScan.uploadFile": "Upload file",
  "expenseScan.errorNotAuthenticated": "Session expired. Please log in again.",
  "expenseScan.errorQuotaExceeded": "You have reached the monthly AI extraction limit.",
  "expenseScan.errorFileNotFound": "File not found. Please try again.",
  "expenseScan.errorServer": "Server error. Please try again in a few minutes.",
  "expenseScan.viewReceipt": "View receipt",
  "expenseScan.viewReceipts": "View receipts",
  "expenseScan.deleteReceipt": "Delete receipt",
  "expenseScan.receiptDeleted": "Receipt deleted successfully",
  "expenseScan.deleteError": "Error deleting receipt",
  "expenseScan.uploadedReceipts": "Uploaded receipts",
  "expenseScan.receipt": "Receipt",
  "expenseScan.addMoreReceipts": "You can add more receipts",
  "expenseScan.addAnotherReceipt": "Add another receipt",

  // Odometer — Pro only
  "settings.tabOdometer": "Odometer",
  "odometer.title": "Odometer",
  "odometer.subtitle": "Odometer readings",
  "odometer.addReading": "+ Add reading",
  "odometer.date": "Date",
  "odometer.readingKm": "Reading (km)",
  "odometer.source": "Source",
  "odometer.notes": "Notes",
  "odometer.photo": "Photo",
  "odometer.totalKm": "Total km in period",
  "odometer.workKm": "Work km",
  "odometer.privateKm": "Private km",
  "odometer.workPct": "Work usage",
  "odometer.proOnly": "Pro feature",
  "odometer.proOnlyBody": "Odometer tracking is a Pro feature to calculate work vs private km ratio (tax compliance).",
  "odometer.upgradeCta": "View plans",
  "odometer.sourceItv": "ITV / TÜV",
  "odometer.sourceTaller": "Workshop / Service",
  "odometer.sourceSeguro": "Insurance",
  "odometer.sourceManual": "Manual entry",
  "odometer.extractingAi": "Extracting km with AI…",
  "odometer.extractedByAi": "AI",
  "odometer.editedManual": "Manual",
  "odometer.userEdited": "Corrected",
  "odometer.correctionNoteLabel": "Correction reason",
  "odometer.correctionNotePlaceholder": "e.g. AI extraction error, value manually corrected",
  "odometer.editKmLabel": "Edit km value",
  "odometer.toastCorrected": "Value updated",
  "odometer.userModifiedTag": "user-modified",
  "odometer.extractionFailed": "AI failed — edit manually",
  "odometer.deleteConfirm": "Delete this odometer reading?",
  "odometer.noSnapshots": "No readings yet. Upload your first odometer photo.",
  "odometer.oneSnapshot": "✓ First reading saved. Add a second to see the usage ratio.",
  "odometer.calcTitle": "Odometer — km breakdown",
  "odometer.yearLabel": "Year",
  "odometer.toastAdded": "Reading added",
  "odometer.toastDeleted": "Reading deleted",
  "odometer.toastSaveError": "Error saving reading",
  "odometer.toastExtractError": "AI extraction failed. Enter the km manually.",
  "odometer.thumbnailAlt": "Odometer photo",
  "odometer.dialogTitle": "Add odometer reading",
  "odometer.dialogSave": "Save reading",
  "odometer.pctLabel": "{pct}% work usage",
  "odometer.qrButton": "Use phone camera",
  "odometer.qrTitle": "Scan with your phone",
  "odometer.qrBody": "Open your phone camera and scan the QR code to photograph the odometer directly with your phone.",
  "odometer.qrWaiting": "Waiting for photo from phone…",
  "odometer.qrSuccess": "Photo received! Processing with AI…",
  "odometer.qrExpiredModal": "QR code expired. Close and try again.",
  "odometer.captureTitle": "Odometer photo",
  "odometer.captureInstructions": "Point the camera at the dashboard odometer display and make sure the numbers are clearly visible.",
  "odometer.captureTakePhoto": "Take photo with camera",
  "odometer.captureSubmit": "Send photo",
  "odometer.captureSuccess": "✓ Photo sent! You can close this window.",
  "odometer.captureError": "Error sending the photo. Please try again.",
  "odometer.captureExpired": "This link has expired. Generate a new QR code from the computer.",
  "odometer.captureLoading": "Preparing…",
  "odometer.captureOr": "Or choose from gallery",

};
